<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ColumnConfig } from '@/modules/documents-new/composables/useColumnVisibility'

const props = defineProps<{
  modelValue: boolean
  columns: ColumnConfig[]
  visibleKeys: string[]
  showFolders: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  save: [payload: { keys: string[]; showFolders: boolean }]
}>()

const localKeys = ref<string[]>([])
const localShowFolders = ref(true)

watch(() => props.modelValue, open => {
  if (open) {
    localKeys.value = [...props.visibleKeys]
    localShowFolders.value = props.showFolders
  }
})

const allSelected = computed(() =>
  props.columns.every(col => localKeys.value.includes(col.key)),
)

const isChecked = (key: string) => localKeys.value.includes(key)

const toggle = (key: string) => {
  const idx = localKeys.value.indexOf(key)
  if (idx > -1) localKeys.value.splice(idx, 1)
  else localKeys.value.push(key)
}

const toggleAll = () => {
  if (allSelected.value) localKeys.value = []
  else localKeys.value = props.columns.map(c => c.key)
}

const half = computed(() => Math.ceil(props.columns.length / 2))
const leftColumns = computed(() => props.columns.slice(0, half.value))
const rightColumns = computed(() => props.columns.slice(half.value))

const close = () => emit('update:modelValue', false)

const save = () => {
  emit('save', {
    keys: [...localKeys.value],
    showFolders: localShowFolders.value,
  })
  close()
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="532"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="column-settings-card">
      <div class="column-settings-header">
        <span class="column-settings-title">Настройка списка документов</span>
        <VBtn
          icon
          variant="text"
          size="small"
          @click="close"
        >
          <VIcon icon="ri-close-line" size="20" />
        </VBtn>
      </div>

      <div class="column-settings-body">
        <div class="column-settings-grid">
          <div class="column-settings-col">
            <label class="column-settings-item" @click.prevent="toggleAll">
              <VCheckbox
                :model-value="allSelected"
                hide-details
                density="compact"
                @update:model-value="toggleAll"
                @click.stop
              />
              <span class="column-settings-label">Выбрать все</span>
            </label>

            <label
              v-for="col in leftColumns"
              :key="col.key"
              class="column-settings-item"
              @click.prevent="toggle(col.key)"
            >
              <VCheckbox
                :model-value="isChecked(col.key)"
                hide-details
                density="compact"
                @update:model-value="toggle(col.key)"
                @click.stop
              />
              <span class="column-settings-label">{{ col.label }}</span>
            </label>
          </div>

          <div class="column-settings-col">
            <div class="column-settings-item column-settings-item--spacer" />

            <label
              v-for="col in rightColumns"
              :key="col.key"
              class="column-settings-item"
              @click.prevent="toggle(col.key)"
            >
              <VCheckbox
                :model-value="isChecked(col.key)"
                hide-details
                density="compact"
                @update:model-value="toggle(col.key)"
                @click.stop
              />
              <span class="column-settings-label">{{ col.label }}</span>
            </label>
          </div>
        </div>

        <div class="column-settings-divider" />

        <label class="column-settings-folders">
          <VSwitch
            v-model="localShowFolders"
            hide-details
            density="compact"
            color="primary"
          />
          <span class="column-settings-label">Отображать папки в таблице</span>
        </label>
      </div>

      <div class="column-settings-actions">
        <VBtn
          variant="outlined"
          color="grey"
          class="px-6"
          @click="close"
        >
          Отменить
        </VBtn>
        <VBtn
          variant="elevated"
          color="primary"
          class="px-6"
          @click="save"
        >
          Сохранить
        </VBtn>
      </div>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.column-settings-card {
  border-radius: 12px !important;
  padding: 24px;
}

.column-settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.column-settings-title {
  font-size: 20px;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.column-settings-body {
  margin-bottom: 24px;
}

.column-settings-grid {
  display: flex;
  gap: 16px;
}

.column-settings-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.column-settings-item {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 0;
  min-height: 36px;

  &--spacer {
    cursor: default;
  }

  :deep(.v-selection-control) {
    min-height: auto;
  }

  :deep(.v-selection-control__wrapper) {
    height: 20px;
  }
}

.column-settings-divider {
  height: 1px;
  margin: 16px 0;
  background-color: rgba(var(--v-theme-on-surface), 0.12);
}

.column-settings-folders {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  min-height: 36px;

  :deep(.v-selection-control) {
    min-height: auto;
  }

  :deep(.v-switch__track) {
    opacity: 1;
  }
}

.column-settings-label {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.87);
  user-select: none;
  white-space: nowrap;
}

.column-settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  :deep(.v-btn) {
    text-transform: none;
    font-weight: 500;
  }
}
</style>
